import React, {useCallback, useLayoutEffect, useRef, useState} from "react";
import '../../styles/components/lesson/settings-selector.scss';
import LessonPlayerSettings, {isBirdsEyeKeyboardVisible} from "../../models/view/lessonPage/LessonPlayerSettings";
import ControlBarDropDown from "./ControlBarDropDown";
import {
  changeNotationLayout,
  changePageCount,
  toggleFretboardVisibility,
  toggleHighlightCurrentMeasure,
  toggleNoteNamesVisibility,
  togglePlayheadVisibility,
  toggleProportionalNotation,
  toggleStaffFingeringsVisibility,
} from "../../redux/lesson/lessonActions";
import ControlBarMenuItemGroup from "./ControlBarMenuItemGroup";
import ControlBarDropDownMenuItem from "./ControlBarDropDownMenuItem";
import Toggle from "../common/Toggle";
import DecrementIncrementButtons from "../common/DecrementIncrementButtons";
import RangeInput from "../common/RangeInput";
import {FontIcon} from "../common/FontIcon";
import {useIsDeviceDesktop} from "../../services/device/device-info-hooks";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import playbackOrchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {NotationLayoutEnum} from "../../models/view/lessonPage/NotationLayoutEnum";
import {settingsStore} from "../../redux/settings/settingsStore";
import {soundSliceSdk_setZoom} from "../../services/eventBus/soundslice/soundSliceSdk";

interface ISettingsSelectorProps {}

// Add support for the 'inert' property, which is not supported prior to React v19
declare module "react" {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface HTMLAttributes<T> {
    /**
     * Prevents focus from moving to any element inside this DOM element and ignores user events.
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inert
     */
    inert?: "inert";
  }
}

export default function SettingsSelector({}: ISettingsSelectorProps) {
  const isMidiConnected = useAppSelector(lessonPlayerStore.getIsMidiConnected)
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const isSettingsDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsSettingsSelectorDropdownOpen);
  const lessonPlayerSettings = segment ? segment.lessonPlayerSettings : new LessonPlayerSettings();
  const isKeyboardVisible = !!(segment && isBirdsEyeKeyboardVisible(lessonPlayerSettings, segment.mode));
  const playbackControlState = useAppSelector(lessonPlayerStore.getPlaybackControlState);
  const controlBarTogglesEnabledState = useAppSelector(lessonPlayerStore.getControlBarTogglesEnabledState);
  const selectedNotationLayout = useAppSelector(lessonPlayerStore.getSelectedNotationLayout);
  const appliedNotationLayout = useAppSelector(lessonPlayerStore.getAppliedNotationLayout);
  const isNotationSpacedProportionally = useAppSelector(lessonPlayerStore.getIsProportionalSpacingEnabled);
  const isMetronomeEnabled = useAppSelector(() => { return !settingsStore.getIsMetronomeMuted(); });
  const isBackingTrackEnabled = useAppSelector(() => { return !settingsStore.getIsBackingTrackMuted(); });
  const isCountInEnabled = useAppSelector(() => { return !settingsStore.getIsCountInMuted(); });
  const zoom = useAppSelector(lessonPlayerStore.getPlaybackControlState).zoom;
  const proportionalSpacing: number = useAppSelector(() => { return lessonPlayerStore.getSpacingForActiveLesson() ?? lessonPlayerStore.getDefaultProportionalSpacing(); });
  const isFeedbackEnabled = useAppSelector(lessonPlayerStore.getIsInstantFeedbackEnabled);
  const isDeviceDesktop = useIsDeviceDesktop();
  const caretDownElement = isDeviceDesktop ? <FontIcon icon={"icon-angle-down"}/> : null;
  const iconElement = <div className="settings-button"><FontIcon icon={"icon-sliders-h"}/>{caretDownElement}</div>;
  const {
    noteNamesVisible: areNoteNamesVisible,
    staffFingeringsVisible: areStaffFingeringsVisible,
  } = lessonPlayerSettings;
  const zoomPercent = `${Math.floor ( (zoom + 25) / 50 * 100)}%`;
  const {
    hidePlayheadDuringPlayback: shouldHidePlayheadDuringPlayback,
    isCurrentMeasureHighlighted,
  } = playbackControlState;

  const volumes = useAppSelector(settingsStore.getVolumes);
  const feedbackLabel = isMidiConnected ? "Instant Feedback" : "Feedback";
  const isSegmentVideoOnly = segment?.mode === SegmentModeEnum.play;
  const isSegmentFollowAlong = segment?.mode === SegmentModeEnum.followAlong;

  const proportionalToggleRef = useRef<HTMLDivElement | null>(null);
  const proportionalSliderRef = useRef<HTMLDivElement | null>(null);

  // Automatically scroll the proportion slider control into view if notation is spaced proportionally,
  // scroll just the toggle control into view if not
  useLayoutEffect(() => {
    const isNotationLayoutHorizontal: boolean = selectedNotationLayout === NotationLayoutEnum.horizontal;

    if (isNotationSpacedProportionally && isNotationLayoutHorizontal) {
      proportionalSliderRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
    }

    if (!isNotationSpacedProportionally && isNotationLayoutHorizontal) {
      proportionalToggleRef?.current?.scrollIntoView({behavior: "smooth", block: "end"})
    }
  }, [isNotationSpacedProportionally, selectedNotationLayout, proportionalSliderRef, proportionalToggleRef]);

  // Automatically scroll the proportion slider control into view when rendered
  useLayoutEffect(() => {
    if (isNotationSpacedProportionally) {
      proportionalSliderRef?.current?.scrollIntoView({behavior: "smooth", block: "end"});
    }
  }, [isNotationSpacedProportionally, proportionalSliderRef]);

  // Set a boolean used to change the opacity of the settings menu so notation layout adjustments can be seen
  // as they are adjusted
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const handleMouseDown =   useCallback(() => setIsMouseDown(true), []);
  const handleMouseUp = useCallback(() => setIsMouseDown(false), []);

  const metronomeVolumeEl = isMetronomeEnabled
    ? <ControlBarDropDownMenuItem>
      <RangeInput
        label={"Metronome"}
        secondaryLabel={`${Math.ceil(volumes.metronome * 100)}%`}
        onChange={(value: number)=>{ settingsStore.setMetronomeVolume(value / 100); }}
        value={volumes.metronome * 100}
        min={0}
        max={100}
        step={1}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const videoVolumeEl = isSegmentVideoOnly || isSegmentFollowAlong
    ? <ControlBarDropDownMenuItem>
      <RangeInput
        label={"Video"}
        secondaryLabel={`${Math.ceil(volumes.video * 100)}%`}
        min={0}
        max={100}
        value={volumes.video * 100}
        onChange={(value: number) => {
          settingsStore.setVideoVolume(value / 100);
        }}
        step={1}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const backingTrackVolumeEl = isBackingTrackEnabled && (!isSegmentVideoOnly && !isSegmentFollowAlong)
    ? <ControlBarDropDownMenuItem>
      <RangeInput
        label={"Backing Track"}
        secondaryLabel={`${Math.ceil(volumes.backingTrack * 100)}%`}
        onChange={(value: number) => {
          settingsStore.setBackingTrackVolume(value / 100);
        }}
        value={volumes.backingTrack * 100}
        min={0}
        max={100}
        step={1}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const countInVolumeEl = isCountInEnabled && (!isSegmentVideoOnly && !isSegmentFollowAlong)
    ? <ControlBarDropDownMenuItem>
      <RangeInput
        label={"Count In"}
        secondaryLabel={`${Math.ceil(volumes.countIn * 100)}%`}
        onChange={(value: number) => {
          settingsStore.setCountInVolume(value / 100);
        }}
        value={volumes.countIn * 100}
        min={0}
        max={100}
        step={1}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const metronomeToggleEl = !isSegmentVideoOnly && !isSegmentFollowAlong
    ? <ControlBarDropDownMenuItem>
      <Toggle
        label="Metronome"
        isToggledOn={isMetronomeEnabled}
        onToggle_toggled={settingsStore.toggleMetronome}
        disabled={!controlBarTogglesEnabledState.isMetronomeToggleEnabled}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const backingTrackToggleEl = !isSegmentVideoOnly && !isSegmentFollowAlong
    ? <ControlBarDropDownMenuItem>
      <Toggle
        label="Backing Track"
        isToggledOn={isBackingTrackEnabled}
        onToggle_toggled={settingsStore.toggleBackingTrack}
        disabled={!controlBarTogglesEnabledState.isBackingTrackToggleEnabled}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const countInToggleEl = !isSegmentVideoOnly && !isSegmentFollowAlong
    ? <ControlBarDropDownMenuItem>
      <Toggle 
        label="Count In"
        isToggledOn={isCountInEnabled}
        onToggle_toggled={settingsStore.toggleCountIn}
        disabled={!controlBarTogglesEnabledState.isCountInToggleEnabled}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const proportionalToggleEl = appliedNotationLayout === NotationLayoutEnum.horizontal
    ? <ControlBarDropDownMenuItem refProp={proportionalToggleRef}>
      <Toggle
        label="Space Notation Proportionally"
        isToggledOn={isNotationSpacedProportionally}
        onToggle_toggled={toggleProportionalNotation}
        disabled={!controlBarTogglesEnabledState.isProportionalToggleEnabled}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const proportionalSliderEl = isNotationSpacedProportionally && appliedNotationLayout === NotationLayoutEnum.horizontal
    ? <ControlBarDropDownMenuItem refProp={proportionalSliderRef} additionalClassName={'visible-when-adjusting'}>
      <RangeInput
        label={"Spacing"}
        secondaryLabel={`${proportionalSpacing}`}
        min={15}
        max={80}
        step={1}
        value={proportionalSpacing}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        onChange={(value: number) => {playbackOrchestrator.setNotationSpacing(value); }}
      />
    </ControlBarDropDownMenuItem>
    : null;

  const isLayoutEnabled =  controlBarTogglesEnabledState.isNotationLayoutToggleEnabled;
  const isCurrentLayoutScrolling = appliedNotationLayout === NotationLayoutEnum.horizontal || appliedNotationLayout === NotationLayoutEnum.vertical;

  function NotationLayoutControl() {
    return (
      <div className={"control-bar-dropdown-menu-item layout-control"} inert={isLayoutEnabled ? undefined : 'inert'}>
        <div className={"label"}>Layout</div>
        <div className={"layout-options"} role={"group"}>
          <div
            role={"button"}
            className={"layout-option"}
            aria-current={isCurrentLayoutScrolling}
            onClick={ () => { playbackOrchestrator.setNotationLayout(NotationLayoutEnum.auto); }}
          >
            Scrolling
          </div>
          <div
            role={"button"}
            className={"layout-option"}
            aria-current={selectedNotationLayout === NotationLayoutEnum.paged}
            onClick={() => { changeNotationLayout(NotationLayoutEnum.paged); }}
          >
            Paged
          </div>
        </div>
      </div>
    );
  }

  const isScrollDirectionEnabled = controlBarTogglesEnabledState.isScrollDirectionToggleEnabled;
  // Only show the scrolling direction control when a scrolling notation layout is active
  const scrollingDirectionControl = (isCurrentLayoutScrolling) ?
    <div className={"control-bar-dropdown-menu-item layout-control"} inert={isScrollDirectionEnabled ? undefined : 'inert'}>
      <div className={"label"}>Direction</div>
      <div className="layout-options" role="group">
        <div
          role={"button"}
          className={"layout-option"}
          aria-label={"Set scroll direction automatically"}
          aria-current={selectedNotationLayout === NotationLayoutEnum.auto}
          onClick={ () => { playbackOrchestrator.setNotationLayout(NotationLayoutEnum.auto); }}
        >
          Auto
        </div>
        <div
          role={"button"}
          className={"layout-option"}
          aria-label={"Scroll vertically"}
          aria-current={selectedNotationLayout === NotationLayoutEnum.vertical}
          onClick={ () => { playbackOrchestrator.setNotationLayout(NotationLayoutEnum.vertical); }}
        >
          <FontIcon icon={"icon-arrow-down"}/>
        </div>
        <div
          role={"button"}
          className={"layout-option"}
          aria-label={"Scroll horizontally"}
          aria-current={selectedNotationLayout === NotationLayoutEnum.horizontal}
          onClick={ () => { playbackOrchestrator.setNotationLayout(NotationLayoutEnum.horizontal); }}
        >
          <FontIcon icon={"icon-arrow-right"} />
        </div>
      </div>
    </div>
    : null;

  const pageCount = useAppSelector(lessonPlayerStore.getPageCount);
  // Only show the page number control when paged notation layout is active
  const pageNumberControl = (selectedNotationLayout === NotationLayoutEnum.paged) ?
    <div className={"control-bar-dropdown-menu-item layout-control"}>
      <div className={"label"}>Pages</div>
      <div className={"layout-options"} role={"group"}>
        <div
          role={"button"}
          className={"layout-option"}
          aria-current={pageCount === 0} // Auto pagination
          onClick={() => { changePageCount(0); }}
        >Auto</div>
        <div
          role={"button"}
          className={"layout-option"}
          aria-current={pageCount === 1}
          onClick={() => { changePageCount(1); }}
        >1</div>
        <div
          role={"button"}
          className={"layout-option"}
          aria-current={pageCount === 2}
          onClick={() => { changePageCount(2); }}
        >2</div>
      </div>
    </div>
    : null;

  const notationFeaturesSection = !isSegmentVideoOnly
    ? <ControlBarMenuItemGroup label={"Notation Features"}>
      <ControlBarDropDownMenuItem>
        <Toggle
          label="Finger Numbers"
          isToggledOn={areStaffFingeringsVisible}
          onToggle_toggled={toggleStaffFingeringsVisibility}
          disabled={!controlBarTogglesEnabledState.isFingerNumbersToggleEnabled}
        />
      </ControlBarDropDownMenuItem>
      <ControlBarDropDownMenuItem>
        <Toggle
          label="Note Names"
          isToggledOn={areNoteNamesVisible}
          onToggle_toggled={toggleNoteNamesVisibility}
          disabled={!controlBarTogglesEnabledState.isNoteNamesToggleEnabled}
        />
      </ControlBarDropDownMenuItem>
      <ControlBarDropDownMenuItem>
        <Toggle
          label={feedbackLabel}
          isToggledOn={isFeedbackEnabled}
          onToggle_toggled={() => {
            const isInstantFeedbackEnabled = !lessonPlayerStore.getIsInstantFeedbackEnabled();
            lessonPlayerStore.setIsInstantFeedbackEnabled(isInstantFeedbackEnabled);
          }}
          disabled={!controlBarTogglesEnabledState.isInstantFeedbackToggleEnabled}
        />
      </ControlBarDropDownMenuItem>
      <ControlBarDropDownMenuItem>
        <Toggle
          label="Hide Playhead While Playing"
          isToggledOn={shouldHidePlayheadDuringPlayback}
          onToggle_toggled={togglePlayheadVisibility}
          disabled={!controlBarTogglesEnabledState.isPlayheadToggleEnabled}
        />
      </ControlBarDropDownMenuItem>
      <ControlBarDropDownMenuItem>
        <Toggle
          label="Highlight Current Measure"
          isToggledOn={isCurrentMeasureHighlighted}
          onToggle_toggled={toggleHighlightCurrentMeasure}
          disabled={!controlBarTogglesEnabledState.isCurrentMeasureHighlightToggleEnabled}
        />
      </ControlBarDropDownMenuItem>
    </ControlBarMenuItemGroup>
    : null;


  /**
    * Takes a zoom value and returns an adjusted value to ensure the zoom value is within the safe range.
    *
    * @param zoom - The current zoom value.
    * @returns The zoom value clamped within the range of minZoom and maxZoom.
    */
  function calculateSafeRangeZoomValue(zoom: number){
    const minZoom = -25;
    const maxZoom = 50;

    if (zoom < minZoom) {
      return minZoom;
    }

    if (zoom > maxZoom) {
      return maxZoom;
    }

    return zoom;
  }

  const zoomBy = useCallback((delta: number) => {
    const incrementedZoom = calculateSafeRangeZoomValue(zoom + delta);
    soundSliceSdk_setZoom(incrementedZoom);
  }, [zoom]);

  const notationLayoutSection = !isSegmentVideoOnly
    ? <ControlBarMenuItemGroup label="Notation Layout">
      <ControlBarDropDownMenuItem>
        <DecrementIncrementButtons
          label="Notation Zoom"
          onIncrementClick={() => zoomBy(5)}
          onDecrementClick={() => zoomBy(-5)}
          incrementButtonEnabled={true}
          decrementButtonEnabled={true}
          subLabel={zoomPercent}
          disabled={!controlBarTogglesEnabledState.isZoomButtonEnabled}
        />
      </ControlBarDropDownMenuItem>
      <NotationLayoutControl />
      {scrollingDirectionControl}
      {pageNumberControl}
      {proportionalToggleEl}
      {proportionalSliderEl}
    </ControlBarMenuItemGroup>
    : null;

  return (
    <ControlBarDropDown
      additionalCssClassName={`settings-selector ${isMouseDown ? `adjusting-layout` : ``}`}
      tooltip={"Settings"}
      ariaLabel={"Settings"}
      isDropdownMenuOpen={isSettingsDropdownSelectorOpen}
      changeDropdownMenuOpen={(isOpen: boolean) => lessonPlayerStore.setIsSettingsSelectorDropdownOpen(isOpen)}
      iconElement={iconElement}>
      <ControlBarMenuItemGroup>
        {metronomeToggleEl}
        {backingTrackToggleEl}
        {countInToggleEl}
        <ControlBarDropDownMenuItem>
          <Toggle
            label="Keyboard"
            isToggledOn={isKeyboardVisible}
            onToggle_toggled={toggleFretboardVisibility}
            disabled={!controlBarTogglesEnabledState.isKeyboardToggleEnabled}
          />
        </ControlBarDropDownMenuItem>
      </ControlBarMenuItemGroup>

      <ControlBarMenuItemGroup label={"Volumes"}>
        {metronomeVolumeEl}
        {videoVolumeEl}
        {backingTrackVolumeEl}
        <ControlBarDropDownMenuItem>
          <RangeInput
            label={"Piano"}
            secondaryLabel={`${Math.ceil(volumes.piano * 100)}%`}
            onChange={(value: number) => {
              settingsStore.setPianoVolume(value / 100);
            }}
            value={volumes.piano * 100}
            min={0}
            max={100}
            step={1}
          />
        </ControlBarDropDownMenuItem>
        {countInVolumeEl}
      </ControlBarMenuItemGroup>
      {notationFeaturesSection}
      {notationLayoutSection}
    </ControlBarDropDown>
  );
}
